import { useCallback } from "react";
import wsIOClient, { Socket } from "socket.io-client";

const PRODUCTION_WEBSOCKET_URL = "https://api.vivasesaude.com.br";
const LOCAL_WEBSOCKET_URL = "http://localhost:3097";
const PRODUCTION_WEBSOCKET_HOST = "api.vivasesaude.com.br";

export const getWebSocketUrl = () => {
  const hostname = window.location.hostname;
  const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";
  const websocketUrl = process.env.REACT_APP_WEBSOCKET_URL;

  if (isLocalhost && (!websocketUrl || websocketUrl.includes(PRODUCTION_WEBSOCKET_HOST))) {
    return LOCAL_WEBSOCKET_URL;
  }

  if (websocketUrl) {
    return websocketUrl;
  }

  return isLocalhost ? LOCAL_WEBSOCKET_URL : PRODUCTION_WEBSOCKET_URL;
};

const activeSockets = new Map<string, Socket>();
const pendingReleases = new Map<string, ReturnType<typeof setTimeout>>();

const buildSocketKey = (room: string) => `${getWebSocketUrl()}::${room}`;

const cancelPendingRelease = (socketKey: string) => {
  const pending = pendingReleases.get(socketKey);
  if (pending) {
    clearTimeout(pending);
    pendingReleases.delete(socketKey);
  }
};

const destroySocket = (socketKey: string) => {
  const socket = activeSockets.get(socketKey);
  if (!socket) {
    return;
  }

  socket.removeAllListeners();
  socket.disconnect();
  activeSockets.delete(socketKey);
};

export const useWebhook = ({ room }: { room: string }) => {
  const getSocket = useCallback(async (): Promise<Socket> => {
    const socketKey = buildSocketKey(room);
    cancelPendingRelease(socketKey);

    const existingSocket = activeSockets.get(socketKey);
    if (existingSocket && !existingSocket.disconnected) {
      return existingSocket;
    }

    if (existingSocket) {
      destroySocket(socketKey);
    }

    const socket = wsIOClient(getWebSocketUrl(), {
      extraHeaders: { room },
      transports: ["polling", "websocket"],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });

    activeSockets.set(socketKey, socket);
    return socket;
  }, [room]);

  return { getSocket };
};

export const releaseWebhookSocket = (room: string, delayMs = 250) => {
  const socketKey = buildSocketKey(room);
  cancelPendingRelease(socketKey);

  if (delayMs <= 0) {
    destroySocket(socketKey);
    return;
  }

  pendingReleases.set(
    socketKey,
    setTimeout(() => {
      pendingReleases.delete(socketKey);
      destroySocket(socketKey);
    }, delayMs),
  );
};
